import pb from '../lib/pocketbase';

export interface InvoiceItem {
  ref: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface Invoice {
  id: string;
  vendor: string;
  date: string;
  total: number;
  items?: InvoiceItem[];
  userId: string;
  created: string;
  updated: string;
  imgUrl?: string;
}

export interface InvoicesListFilters {
  month?: number | null;
  year?: number | null;
}

export interface InvoicesListProps {
  onInvoiceClick?: (invoice: Invoice) => void;
  userId: string;
  storeId?: string;
  onDataChange?: (invoices: Invoice[]) => void;
}

export class InvoicesList {
  private container: HTMLElement;
  private props: InvoicesListProps;
  private invoices: Invoice[] = [];
  private isLoading: boolean = true;
  private error: string | null = null;
  private filters: InvoicesListFilters = {};

  constructor(container: HTMLElement, props: InvoicesListProps) {
    this.container = container;
    this.props = props;
    this.init();
  }

  private async init(): Promise<void> {
    await this.loadInvoices();
    this.render();
  }

  private async loadInvoices(): Promise<void> {
    try {
      this.isLoading = true;
      this.error = null;
      this.render();

      // Obtener facturas del usuario actual desde PocketBase
      const records = await pb.collection('invoices').getFullList({
        filter: this.buildFilter(),
        sort: '-created',
      });

      this.invoices = records as unknown as Invoice[];
      this.isLoading = false;
      this.render();
    } catch (error: any) {
      console.error('Error al cargar facturas:', error);
      this.error = 'Error al cargar las facturas. Por favor, intenta de nuevo.';
      this.isLoading = false;
      this.render();
    }
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  private buildFilter(): string {
    const filters: string[] = [];

    if (this.props.storeId) {
      filters.push(`storeId = "${this.props.storeId}"`);
    } else {
      filters.push(`userId = "${this.props.userId}"`);
    }

    const month = this.filters.month ?? null;
    const year = this.filters.year ?? null;

    if (month && year) {
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 1);

      const startIso = start.toISOString();
      const endIso = end.toISOString();

      filters.push(`date >= "${startIso}" && date < "${endIso}"`);
    }

    return filters.join(' && ');
  }

  private getHeaderTitle(): string {
    if (this.props.storeId) {
      return 'Facturas de la tienda';
    }

    const month = this.filters.month ?? null;
    const year = this.filters.year ?? null;

    if (month && year) {
      const formatted = this.formatMonthYear(month, year);
      return `Facturas ${formatted}`;
    }

    return 'Mis Facturas';
  }

  private formatMonthYear(month: number, year: number): string {
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    });
  }

  private render(): void {
    if (this.isLoading) {
      this.container.innerHTML = `
        <div class="invoices-list">
          <div class="invoices-header">
            <h3>${this.getHeaderTitle()}</h3>
          </div>
          <div class="loading-state">
            <p>⏳ Cargando facturas...</p>
          </div>
        </div>
      `;
      return;
    }

    if (this.error) {
      this.container.innerHTML = `
        <div class="invoices-list">
          <div class="invoices-header">
            <h3>${this.getHeaderTitle()}</h3>
          </div>
          <div class="error-state">
            <p>❌ ${this.error}</p>
            <button class="retry-btn">Reintentar</button>
          </div>
        </div>
      `;
      this.attachRetryListener();
      return;
    }

    if (this.invoices.length === 0) {
      this.container.innerHTML = `
        <div class="invoices-list">
          <div class="invoices-header">
            <h3>${this.getHeaderTitle()}</h3>
          </div>
          <div class="empty-state">
            <p>📭 No tienes facturas aún</p>
            <p class="empty-subtitle">Escanea tu primera factura para comenzar</p>
          </div>
        </div>
      `;
      return;
    }

    const invoicesHTML = this.invoices.map(invoice => `
      <div class="invoice-item" data-invoice-id="${invoice.id}">
        <div class="invoice-content">
          <div class="invoice-vendor">
            <span class="vendor-icon">🏪</span>
            <span class="vendor-name">${this.escapeHtml(invoice.vendor)}</span>
          </div>
          <div class="invoice-details">
            <div class="invoice-date">
              <span class="detail-label">📅</span>
              <span class="detail-value">${this.formatDate(invoice.date)}</span>
            </div>
            <div class="invoice-total">
              <span class="detail-label">💰</span>
              <span class="detail-value total-amount">${this.formatCurrency(invoice.total)}</span>
            </div>
          </div>
        </div>
        <div class="invoice-actions">
          <button class="view-btn" data-invoice-id="${invoice.id}">Ver</button>
          <button class="delete-btn" data-invoice-id="${invoice.id}">🗑️</button>
        </div>
      </div>
    `).join('');

    this.container.innerHTML = `
      <div class="invoices-list">
          <div class="invoices-header">
            <h3>${this.getHeaderTitle()}</h3>
          <span class="invoices-count">${this.invoices.length} factura${this.invoices.length !== 1 ? 's' : ''}</span>
        </div>
        <div class="invoices-container">
          ${invoicesHTML}
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const invoiceItems = this.container.querySelectorAll('.invoice-item');
    invoiceItems.forEach(item => {
      item.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.view-btn') && !target.closest('.delete-btn')) {
          const invoiceId = item.getAttribute('data-invoice-id');
          const invoice = this.invoices.find(inv => inv.id === invoiceId);
          if (invoice && this.props.onInvoiceClick) {
            this.props.onInvoiceClick(invoice);
          }
        }
      });
    });

    const viewButtons = this.container.querySelectorAll('.view-btn');
    viewButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const invoiceId = (btn as HTMLElement).getAttribute('data-invoice-id');
        const invoice = this.invoices.find(inv => inv.id === invoiceId);
        if (invoice && this.props.onInvoiceClick) {
          this.props.onInvoiceClick(invoice);
        }
      });
    });

    const deleteButtons = this.container.querySelectorAll('.delete-btn');
    deleteButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const invoiceId = (btn as HTMLElement).getAttribute('data-invoice-id');
        const invoice = this.invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          this.confirmDelete(invoice);
        }
      });
    });
  }

  private attachRetryListener(): void {
    const retryBtn = this.container.querySelector('.retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        this.loadInvoices();
      });
    }
  }

  private confirmDelete(invoice: Invoice): void {
    // Crear modal de confirmación
    const modal = document.createElement('div');
    modal.className = 'delete-confirmation-modal';
    modal.innerHTML = `
      <div class="modal-overlay">
        <div class="modal-content">
          <h3>⚠️ Confirmar Eliminación</h3>
          <p>¿Estás seguro de que deseas eliminar esta factura?</p>
          <div class="invoice-summary">
            <strong>${this.escapeHtml(invoice.vendor)}</strong>
            <span>${this.formatDate(invoice.date)}</span>
            <span class="amount">${this.formatCurrency(invoice.total)}</span>
          </div>
          <p class="warning-text">Esta acción no se puede deshacer.</p>
          <div class="modal-actions">
            <button class="cancel-btn">Cancelar</button>
            <button class="confirm-delete-btn">Eliminar</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Event listeners para el modal
    const cancelBtn = modal.querySelector('.cancel-btn');
    const confirmBtn = modal.querySelector('.confirm-delete-btn');
    const overlay = modal.querySelector('.modal-overlay');

    const closeModal = () => {
      document.body.removeChild(modal);
    };

    if (cancelBtn) {
      cancelBtn.addEventListener('click', closeModal);
    }

    if (confirmBtn) {
      confirmBtn.addEventListener('click', async () => {
        closeModal();
        await this.deleteInvoice(invoice.id);
      });
    }

    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          closeModal();
        }
      });
    }
  }

  private async deleteInvoice(invoiceId: string): Promise<void> {
    try {
      // Mostrar indicador de carga
      const loadingToast = this.showToast('⏳ Eliminando factura...', 'loading');

      // Eliminar usando la API personalizada
      const apiBaseUrl = this.getApiBaseUrl()
      const response = await fetch(`${apiBaseUrl}/api/invoice/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoiceId })
      });
      if (!response.ok) {
        throw new Error(`Error al eliminar la factura: ${response.statusText}`);
      }

      // Remover el toast de carga
      if (loadingToast.parentElement) {
        document.body.removeChild(loadingToast);
      }

      // Mostrar toast de éxito
      this.showToast('✅ Factura eliminada correctamente', 'success');

      // Recargar la lista
      await this.loadInvoices();
      if (this.props.onDataChange) {
        this.props.onDataChange(this.invoices);
      }
    } catch (error: any) {
      console.error('Error al eliminar factura:', error);
      this.showToast('❌ Error al eliminar la factura', 'error');
    }
  }

  private showToast(message: string, type: 'loading' | 'success' | 'error'): HTMLElement {
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    // Auto-remover después de 3 segundos (excepto loading)
    if (type !== 'loading') {
      setTimeout(() => {
        if (toast.parentElement) {
          document.body.removeChild(toast);
        }
      }, 3000);
    }

    return toast;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  public async refresh(): Promise<void> {
    await this.loadInvoices();
    if (this.props.onDataChange) {
      this.props.onDataChange(this.invoices);
    }
  }

  public updateProps(newProps: Partial<InvoicesListProps>): void {
    const previousStoreId = this.props.storeId;
    this.props = { ...this.props, ...newProps };

    const storeIdChanged =
      Object.prototype.hasOwnProperty.call(newProps, 'storeId') &&
      newProps.storeId !== previousStoreId;

    if (storeIdChanged) {
      this.filters = {};
    }

    this.loadInvoices();
  }

  public setFilters(filters?: InvoicesListFilters | null): void {
    this.filters = filters ?? {};
    this.loadInvoices();
  }
  
  private getApiBaseUrl(): string {
    // Verificar si existe la configuración global (generada dinámicamente en Docker)
    if (typeof window !== 'undefined' && (window as any).APP_CONFIG) {
      const config = (window as any).APP_CONFIG
      return config.API_BASE_URL || config.VITE_POCKETBASE_URL || 'http://localhost:8089'
    }

    // Fallback a variables de entorno de Vite (desarrollo)
    return import.meta.env.VITE_POCKETBASE_URL || 'http://localhost:8089'
  }
}

